import React from 'react';
import { ExclamationCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  action?: {
    label: string;
    onClick: () => void;
  };
  'data-testid'?: string;
}

/**
 * ErrorState Component
 *
 * Reusable error state component with retry functionality.
 *
 * Features:
 * - Error icon
 * - Customizable title and message
 * - Retry button
 * - Optional custom action button
 * - Responsive design
 *
 * @example
 * <ErrorState
 *   title="Failed to load courses"
 *   message="We couldn't load your courses. Please try again."
 *   onRetry={() => refetch()}
 * />
 */
const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Something went wrong',
  message = 'An error occurred while loading this content. Please try again.',
  onRetry,
  action,
  'data-testid': testId = 'error-state',
}) => {
  return (
    <div
      className="flex flex-col items-center justify-center p-8 text-center"
      data-testid={testId}
    >
      {/* Error Icon */}
      <div
        className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-error-100 dark:bg-error-900/20"
        data-testid={`${testId}-icon`}
      >
        <ExclamationCircleIcon className="w-10 h-10 text-error-600 dark:text-error-400" />
      </div>

      {/* Title */}
      <h3
        className="text-lg font-semibold text-text-primary mb-2"
        data-testid={`${testId}-title`}
      >
        {title}
      </h3>

      {/* Message */}
      <p
        className="text-text-secondary max-w-md mb-6"
        data-testid={`${testId}-message`}
      >
        {message}
      </p>

      {/* Actions */}
      <div className="flex items-center gap-3">
        {/* Retry Button */}
        {onRetry && (
          <button
            onClick={onRetry}
            className="btn-primary flex items-center gap-2"
            data-testid={`${testId}-retry-button`}
          >
            <ArrowPathIcon className="w-4 h-4" />
            <span>Try Again</span>
          </button>
        )}

        {/* Custom Action Button */}
        {action && (
          <button
            onClick={action.onClick}
            className="btn-outline"
            data-testid={`${testId}-action-button`}
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
};

export default ErrorState;
